import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';

import React, { useCallback, useState } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';

import Icon from '@ui/Icon';
import { MenuItem } from '@ui/Menu';

import { useMergeBranchModalContext } from '../Modals';

import styles from './index.module.scss';

interface MergeBranchMenuItemProps {
  branch: BranchRepresentationType;
}

function MergeBranchMenuItem({ branch }: MergeBranchMenuItemProps) {
  const bem = useClassy(styles, 'MergeBranchMenuItem');
  const { setBranchToMerge } = useMergeBranchModalContext();
  const [getMergeStatus, canUserMerge] = useSuperHubStore(s => [s.branches.getMergeStatus, s.branches.canUserMerge]);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  const handleMerge = useCallback(async () => {
    setIsCheckingStatus(true);
    const { data: mergeStatus } = await getMergeStatus(branch.name);
    setBranchToMerge(branch, mergeStatus);
    setIsCheckingStatus(false);
  }, [branch, getMergeStatus, setBranchToMerge]);

  if (!canUserMerge) {
    return (
      <MenuItem className={bem('_restricted')} TagName="span">
        Merging is restricted to admins.
      </MenuItem>
    );
  }

  return (
    <MenuItem
      aria-label="Merge Branch"
      icon={<Icon aria-hidden="true" name="git-merge" />}
      loading={isCheckingStatus}
      onClick={handleMerge}
      TagName="button"
    >
      <span>Merge to v{branch.base.name}</span>
    </MenuItem>
  );
}

export default MergeBranchMenuItem;
